
import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.addNavigationControls();
    this.addPopupStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  addNavigationControls() {
    const dialog = this.refs.dialog;

    if (dialog.querySelector('.custom-zoom-arrow')) return;

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'custom-zoom-arrow custom-zoom-arrow--previous';
    previous.setAttribute('aria-label', 'Previous image');
    previous.innerHTML = '&#10094;';
    previous.addEventListener('click', () => this.previousMedia());

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'custom-zoom-arrow custom-zoom-arrow--next';
    next.setAttribute('aria-label', 'Next image');
    next.innerHTML = '&#10095;';
    next.addEventListener('click', () => this.nextMedia());

    dialog.append(previous, next);
  }

  addPopupStyles() {
    if (document.getElementById('custom-product-zoom-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-product-zoom-styles';

    style.textContent = `
      zoom-dialog .dialog-zoomed-gallery__dialog {
        position: fixed !important;
        inset: 0 !important;
        box-sizing: border-box !important;
        width: 100vw !important;
        max-width: 100vw !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: transparent !important;
        color: #fff !important;
        overflow: hidden !important;
      }

      zoom-dialog .dialog-zoomed-gallery__dialog::backdrop {
        background: rgba(0, 0, 0, 0.48) !important;
      }

      zoom-dialog .dialog-zoomed-gallery {
        box-sizing: border-box !important;
        display: flex !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100dvh !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        scroll-snap-type: x mandatory !important;
        scroll-behavior: smooth !important;
        scrollbar-width: none !important;
      }

      zoom-dialog .dialog-zoomed-gallery::-webkit-scrollbar {
        display: none !important;
      }

      zoom-dialog .dialog-zoomed-gallery > .product-media-container {
        box-sizing: border-box !important;
        position: relative !important;
        display: flex !important;
        flex: 0 0 100% !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        min-width: 100% !important;
        height: 100% !important;
        max-height: 100dvh !important;
        padding: 55px 70px 100px 30px !important;
        overflow: hidden !important;
        scroll-snap-align: start !important;
      }

      zoom-dialog .dialog-zoomed-gallery .product-media__drag-zoom-wrapper {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        overflow: hidden !important;
      }

      zoom-dialog .dialog-zoomed-gallery .product-media-container--image .product-media {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        aspect-ratio: auto !important;
        overflow: hidden !important;
      }

      zoom-dialog .dialog-zoomed-gallery img.product-media__image {
        display: block !important;
        width: auto !important;
        height: auto !important;
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: contain !important;
        object-position: center !important;
        transform: none !important;
      }

      zoom-dialog .custom-zoom-arrow {
        position: fixed !important;
        top: 50% !important;
        z-index: 10000 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 46px !important;
        height: 46px !important;
        padding: 0 !important;
        border: 1px solid rgba(255,255,255,.7) !important;
        border-radius: 50% !important;
        background: rgba(255,255,255,.92) !important;
        color: #111 !important;
        font-size: 22px !important;
        line-height: 1 !important;
        cursor: pointer !important;
        transform: translateY(-50%) !important;
      }

      zoom-dialog .custom-zoom-arrow--previous {
        left: 18px !important;
      }

      zoom-dialog .custom-zoom-arrow--next {
        right: 18px !important;
      }

      zoom-dialog .dialog-zoomed-gallery__close-button {
        position: fixed !important;
        top: 16px !important;
        right: 18px !important;
        z-index: 10001 !important;
        color: #fff !important;
      }

      zoom-dialog .dialog-thumbnails-list-container {
        z-index: 10000 !important;
      }

      @media (max-width: 749px) {
        zoom-dialog .dialog-zoomed-gallery > .product-media-container {
          padding: 55px 12px 95px !important;
        }

        zoom-dialog .dialog-zoomed-gallery img.product-media__image {
          max-width: 94vw !important;
          max-height: 75dvh !important;
        }

        zoom-dialog .custom-zoom-arrow {
          width: 36px !important;
          height: 36px !important;
          font-size: 18px !important;
        }

        zoom-dialog .custom-zoom-arrow--previous {
          left: 8px !important;
        }

        zoom-dialog .custom-zoom-arrow--next {
          right: 8px !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const showDialog = () => {
      dialog.showModal();

      targetImage?.scrollIntoView({ behavior: 'instant' });
      targetThumbnail?.scrollIntoView({ behavior: 'instant' });

      this.selectThumbnail(index, { behavior: 'instant' });
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li, slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage ||
      !targetImage
    ) {
      showDialog();
      return;
    }

    const transitionName = 'gallery-item-open';
    sourceImage.style.setProperty('view-transition-name', transitionName);

    const focalPoint = sourceImage.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    await startViewTransition(() => {
      showDialog();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  getActiveIndex() {
    const { media, thumbnails } = this.refs;

    const selectedThumbnail = Array.from(thumbnails.querySelectorAll('button'))
      .findIndex((button) => button.getAttribute('aria-selected') === 'true');

    if (selectedThumbnail >= 0) return selectedThumbnail;

    const visibleIndex = media.findIndex((item) => {
      const rect = item.getBoundingClientRect();
      return rect.left < window.innerWidth && rect.right > 0;
    });

    return Math.max(visibleIndex, 0);
  }

  previousMedia() {
    const { media } = this.refs;
    if (media.length < 2) return;

    const index = this.getActiveIndex();
    const previousIndex = (index - 1 + media.length) % media.length;

    this.selectThumbnail(previousIndex);
  }

  nextMedia() {
    const { media } = this.refs;
    if (media.length < 2) return;

    const index = this.getActiveIndex();
    const nextIndex = (index + 1) % media.length;

    this.selectThumbnail(nextIndex);
  }

  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) {
      return false;
    }

    const image = mediaContainer.querySelector('img.product-media__image');

    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl)
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
    return true;
  }

  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, index) => {
      button.setAttribute('aria-selected', `${index === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      this.closeDialog();
      return;
    }

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = 'gallery-item-close';

    const mediaGallery = this.closest('media-gallery');
    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      return;
    }

    const focalPoint = slide.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previousMedia();
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextMedia();
    }
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (!thumbnails?.children.length) return;
    if (isNaN(index) || index < 0 || index >= thumbnails.children.length) return;

    const targetThumbnail = thumbnails.children[index];
    if (!(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({ behavior: options.behavior });
      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    if (!elements.length) {
      resolve(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((previous, current) =>
          current.intersectionRatio > previous.intersectionRatio
            ? current
            : previous
        );

        observer.disconnect();
        resolve(mostVisible.target);
      },
      {
        threshold: Array.from({ length: 100 }, (_, index) => index / 100),
      }
    );

    elements.forEach((element) => observer.observe(element));
  });
}
